import type { PortfolioLabInvestmentItem } from "../../ctx";
import { COMPOSITION_GROUP_BY_UNGROUP, CompositionFirstLevel, type CompositionGroupBy } from "../../state";
import { CompositionViewMode } from "./rows";

/** Pure: map the current group-by selection to the grid's view mode. */
export function deriveCompositionViewMode(groupBy: CompositionGroupBy): CompositionViewMode {
	if (groupBy.secondLevel === COMPOSITION_GROUP_BY_UNGROUP) {
		return groupBy.firstLevel === CompositionFirstLevel.holdings
			? CompositionViewMode.holdingsUngroup
			: CompositionViewMode.ungroup;
	}
	return groupBy.firstLevel === CompositionFirstLevel.allocation
		? CompositionViewMode.allocation
		: CompositionViewMode.holdings;
}

/**
 * Equality guard for the rowData projection over `investments$` (R3).
 *
 * WARNING: the derived rowData only recomputes when one of these fields changes BY REFERENCE
 * (`currentComposition`, `initialComposition`, `aum`, `tickerInfoPool`, `name`). Any mutation
 * that updates one of them in place — without replacing the field (and the investment item)
 * by reference — is invisible to this guard and leaves the grid rendering stale rows.
 */
export function compositionEqual(prev: PortfolioLabInvestmentItem[], next: PortfolioLabInvestmentItem[]): boolean {
	if (prev.length !== next.length) {
		return false;
	}
	return prev.every((p, i) => {
		const n = next[i];
		return (
			p.virtualUuid === n.virtualUuid &&
			p.currentComposition === n.currentComposition &&
			p.initialComposition === n.initialComposition &&
			p.aum === n.aum &&
			p.tickerInfoPool === n.tickerInfoPool &&
			p.name === n.name
		);
	});
}

/**
 * Equality guard for the columnDefs projection over `investments$`: column defs depend only on
 * the column set/order (`virtualUuid` drives colIds and the palette index), the group header
 * name and the currency symbol. Weight edits, AUM changes and save-status churn must NOT
 * rebuild defs — layout (width/order) is owned by `compositionColumnState$`, and cell
 * appearance (decimals, saving dim) repaints via the self-subscribing cell renderers.
 */
export function columnDefsEqual(prev: PortfolioLabInvestmentItem[], next: PortfolioLabInvestmentItem[]): boolean {
	if (prev.length !== next.length) {
		return false;
	}
	return prev.every((p, i) => {
		const n = next[i];
		return p.virtualUuid === n.virtualUuid && p.name === n.name && p.currency === n.currency;
	});
}
